import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bed, Maximize2, Layers } from "lucide-react";
import { families, getProductsByFamily } from "../data/products";
import useSEO from "../hooks/useSEO";

function CardStat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">
      <Icon size={14} strokeWidth={1.4} className="text-[var(--yono-wood)]" />
      <span>{label}</span>
    </div>
  );
}

function ProductCard({ p, size = "default" }) {
  const aspectClass = size === "large" ? "aspect-[3/2]" : "aspect-[4/3]";
  const titleSize =
    size === "large" ? "text-[28px] sm:text-[32px]" : "text-[24px]";
  const cardSrc = p.discoverImage || p.cardImage || p.hero;
  return (
    <div className="flex flex-col" data-testid={`gamme-vignette-${p.slug}`}>
      <Link to={`/produits/${p.slug}`} className="block hover-lift">
        <div className={`relative ${aspectClass} product-card-img overflow-hidden`}>
          {cardSrc ? (
            <img
              src={cardSrc}
              alt={p.name}
              className={`w-full h-full ${
                p.cardFit === "contain" ? "object-contain" : "object-cover"
              }`}
            />
          ) : (
            <div className="absolute inset-0 placeholder-bois flex flex-col items-center justify-center">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--yono-warm-grey)] mb-2">
                Visuel à venir
              </span>
              <span className="font-heading text-2xl">{p.name}</span>
            </div>
          )}
        </div>
      </Link>
      <div className="mt-6">
        <div className="eyebrow text-[var(--yono-wood)]">
          YONO · {p.surface} · {p.capacity}
        </div>
        <h3 className={`font-heading ${titleSize} leading-none mt-2`}>
          {p.name}
        </h3>
        <p className="text-[14px] text-[var(--yono-warm-grey)] mt-2">
          {p.tagline}
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
          <CardStat icon={Bed} label={p.capacity} />
          <CardStat icon={Maximize2} label={p.surface} />
          <CardStat icon={Layers} label="CLT bois massif" />
        </div>
        <div className="flex items-center justify-between mt-7">
          <span className="text-[14px] leading-tight">
            <span className="font-medium">Prix sur devis</span>
            <span className="block text-[11px] tracking-[0.05em] text-[var(--yono-warm-grey)] mt-1">
              Selon configuration, équipements et livraison.
            </span>
          </span>
          <Link
            to={`/produits/${p.slug}`}
            className="btn-primary !py-[10px] !px-[18px]"
            data-testid={`vignette-cta-${p.slug}`}
          >
            En savoir plus <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function FamilyHeader({ family }) {
  return (
    <div className="border-t border-[var(--yono-border)] pt-7 mb-8 lg:mb-10">
      <div className="flex items-baseline justify-between gap-6">
        <p className="eyebrow text-[var(--yono-wood)]">
          Famille {family.index}
        </p>
        <span className="hidden sm:inline text-[12px] text-[var(--yono-warm-grey)] tracking-[0.12em] uppercase">
          {family.name}
        </span>
      </div>
      <h2 className="mt-4 font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight leading-[1.15]">
        {family.name}
      </h2>
      <p className="mt-4 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-3xl">
        {family.intro}
      </p>
    </div>
  );
}

function FamilyGrid({ family }) {
  const items = getProductsByFamily(family.slug);
  const count = items.length;

  // 3 products -> 3-col grid (compact)
  // 2 products -> 2-col grid (larger)
  // 1 product  -> editorial split (7/5)
  if (count === 3) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-7 gap-y-14">
        {items.map((p) => (
          <ProductCard key={p.slug} p={p} />
        ))}
      </div>
    );
  }
  if (count === 2) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-14">
        {items.map((p) => (
          <ProductCard key={p.slug} p={p} size="large" />
        ))}
      </div>
    );
  }
  if (count === 1) {
    const p = items[0];
    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-10 items-center">
        <div className="md:col-span-7">
          <ProductCard p={p} size="large" />
        </div>
        <aside className="md:col-span-5 md:pl-4">
          <div className="eyebrow text-[var(--yono-wood)] mb-4">
            Pourquoi {p.name} ?
          </div>
          <h3 className="font-heading text-[24px] sm:text-[28px] leading-[1.15] tracking-tight">
            {p.tagline}
          </h3>
          <p className="mt-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80">
            {p.intro}
          </p>
          <Link
            to={`/produits/${p.slug}`}
            className="btn-outline mt-7 inline-flex"
            data-testid={`family-editorial-cta-${p.slug}`}
          >
            Découvrir {p.name} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </aside>
      </div>
    );
  }
  return null;
}

export default function DecouvrirGamme() {
  useSEO({
    title: "Découvrir la gamme — Trois familles, six habitats bois",
    description:
      "L'offre YONO organisée en trois familles : Micro-habitats, Lodges et Hébergements saisonniers. Six habitats bois CLT pour campings, PRL et domaines nature.",
    path: "/gamme",
    keywords:
      "gamme YONO, micro-habitats, lodges bois, hébergement saisonnier, habitat CLT, alternative mobil-home",
  });

  return (
    <div data-testid="gamme-vignettes-page">
      <section className="pt-10 pb-6">
        <div className="container-yono">
          <div className="eyebrow mb-5">L'offre YONO</div>
          <h1 className="font-heading text-[24px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.05] max-w-4xl">
            Une gamme bois structurée en trois familles
          </h1>
          <div className="mt-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 max-w-3xl space-y-3">
            <p>
              YONO propose une gamme d'hébergements bois compacts, durables et
              prêts à exploiter, conçus pour les campings, PRL, domaines
              nature et sites touristiques.
            </p>
            <p>
              L'offre est organisée en trois familles pour répondre aux
              principaux besoins des exploitants : créer des hébergements
              différenciants sur de petites emprises, proposer une alternative
              bois au mobil-home, et loger les saisonniers directement sur
              site.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-14">
        <div className="container-yono">
          <div className="space-y-16 lg:space-y-24">
            {families.map((f) => (
              <div key={f.slug} data-testid={`family-section-${f.slug}`}>
                <FamilyHeader family={f} />
                <FamilyGrid family={f} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lecture de gamme — tableau de synthèse */}
      <section className="bg-[var(--yono-beige)] py-14">
        <div className="container-yono">
          <p className="eyebrow mb-5">Lecture de gamme</p>
          <h2 className="font-heading text-[24px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] max-w-3xl">
            Six produits, trois familles, une seule exigence
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full text-[14px]" data-testid="lecture-table">
              <thead>
                <tr className="border-b border-[var(--yono-border)] text-left">
                  <th className="py-3 pr-6 eyebrow text-[var(--yono-warm-grey)] font-normal">
                    Famille
                  </th>
                  <th className="py-3 pr-6 eyebrow text-[var(--yono-warm-grey)] font-normal">
                    Produit
                  </th>
                  <th className="py-3 pr-6 eyebrow text-[var(--yono-warm-grey)] font-normal">
                    Surface
                  </th>
                  <th className="py-3 eyebrow text-[var(--yono-warm-grey)] font-normal">
                    Capacité / Usage
                  </th>
                </tr>
              </thead>
              <tbody>
                {families.flatMap((f) =>
                  getProductsByFamily(f.slug).map((p, i) => (
                    <tr
                      key={p.slug}
                      className="border-b border-[var(--yono-border)]"
                    >
                      <td className="py-4 pr-6 text-[var(--yono-warm-grey)]">
                        {i === 0 ? f.name : ""}
                      </td>
                      <td className="py-4 pr-6">
                        <Link
                          to={`/produits/${p.slug}`}
                          className="font-heading text-[16px] hover:text-[var(--yono-wood)] transition-colors"
                        >
                          {p.name}
                        </Link>
                      </td>
                      <td className="py-4 pr-6">{p.surface}</td>
                      <td className="py-4">{p.capacity}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Positionnement */}
      <section className="section">
        <div className="container-yono grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">Positionnement</p>
            <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight leading-[1.15]">
              Une réponse structurée aux besoins de l'hôtellerie de plein air
            </h2>
          </div>
          <div className="lg:col-span-7 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 space-y-4">
            <p>
              Avec cette organisation, YONO ne présente pas seulement une
              collection de surfaces, mais une réponse structurée aux besoins
              de l'hôtellerie de plein air : densifier intelligemment un parc,
              créer une offre différenciante, remplacer progressivement le
              mobil-home par des hébergements bois, et répondre aux besoins
              opérationnels liés au logement des saisonniers.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[var(--yono-beige)] py-14">
        <div className="container-yono text-center">
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="mt-4 text-[var(--yono-warm-grey)] max-w-2xl mx-auto text-[15px] leading-relaxed">
            Notre équipe vous accompagne pour identifier le logement le plus
            adapté à votre projet, vos emplacements et votre stratégie
            d'exploitation.
          </p>
          <Link to="/contact" className="btn-primary mt-7" data-testid="gamme-aide-btn">
            Nous contacter <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
